import axios from 'axios';
import { Auth } from 'aws-amplify';

import { buildApiUrl } from './apiConfig';
import { ApiEnvelope, getApiErrorMessage, unwrapApiData } from './apiResponse';
import {
  CreatePracticeQuestionRequest,
  CreatePracticeQuestionResponse,
  SubmitPracticeAnswerResponse,
} from '../types/lesson';

export interface Module {
  module_id: string;
  school_id: string;
  teacher_id: string;
  title: string;
  subject: string;
  grade_level: string;
  duration_hours: number;
  learning_objectives: string[];
  prerequisites?: string[];
  standards?: string[];
  status: 'draft' | 'published' | 'archived';
  content_hash: string | null;
  unit_count: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

export interface Unit {
  unit_id: string;
  title: string;
  description: string;
  order_index: number;
  duration_minutes: number;
  learning_objectives: string[];
  status: 'draft' | 'outlined' | 'generated' | 'reviewed' | 'published';
  current_content?: S3ContentPointer | null;
  content?: any;
}

export interface AssignmentSummary {
  assignment_id: string;
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  student_id: string;
  student_name: string;
  student_email: string;
  status: string;
  due_date: string | null;
  assigned_at: string;
  completed_at: string | null;
  updated_at: string;
  instructions: string | null;
}

export interface AssignmentModuleGroup {
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  assignments: AssignmentSummary[];
}

export interface TeacherAssignmentsResponse {
  teacher_id: string;
  assignments: AssignmentSummary[];
  total_assignments: number;
  status_summary: {
    assigned: number;
    in_progress: number;
    submitted: number;
    completed: number;
  };
  modules: AssignmentModuleGroup[];
}

export interface StudentPracticeAttempt {
  attempt_id: string;
  unit_id: string;
  unit_title: string;
  set_id: string;
  question_id: string;
  question: string;
  question_type: string;
  student_answer: unknown;
  reference_answer: unknown;
  is_correct: boolean | null;
  attempted_at: string;
  feedback?: import('./feedbackService').FeedbackItem | null;
}

export interface StudentPracticeAttemptsResponse {
  student_id: string;
  student_name: string;
  assignments: Array<{
    assignment_id: string;
    module_id: string;
    module_title: string;
    module_subject: string;
    module_grade_level: string;
    attempts: StudentPracticeAttempt[];
  }>;
  total_attempts: number;
}

export interface CreateModuleRequest {
  title: string;
  subject: string;
  grade_level: string;
  duration_hours: number;
  learning_objectives: string[];
  prerequisites?: string[];
  standards?: string[];
}

export interface CreateUnitRequest {
  title: string;
  description: string;
  duration_minutes: number;
  learning_objectives: string[];
}

export interface GenerateContentRequest {
  module_overview: string;
  module_objectives: string[];
  lesson_number: number;
  lesson_title: string;
  lesson_focus: string;
  previous_lessons_summary?: string;
  subject: string;
  grade_level: string;
  state_standards?: string;
  duration_minutes?: number;
}

export interface GenerateContentResponse {
  message: string;
  unit_id: string;
  content_sections: {
    introduction: boolean;
    main_content: boolean;
    activities: number;
    summary: boolean;
  };
  quiz_questions: number;
}

interface ModuleListApiResponse {
  modules: Module[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages?: number;
  };
}

interface ModuleContentApiResponse {
  module_id: string;
  content?: S3ContentPointer | null;
  units?: Unit[];
  outline?: any;
}

export interface S3ContentPointer {
  s3_key: string;
  content_hash: string;
  version?: number;
  presigned_url?: string;
  expires_at?: string;
}

const normalizeModule = (module: Partial<Module> & Pick<Module, 'module_id' | 'title'>): Module =>
  ({
    subject: '',
    grade_level: '',
    duration_hours: 0,
    learning_objectives: [],
    content_hash: (module as any).current_content?.content_hash || null,
    unit_count: 0,
    ...module,
  } as Module);

const normalizeUnit = (unit: Partial<Unit> & Pick<Unit, 'unit_id' | 'title'>): Unit =>
  ({
    description: '',
    duration_minutes: 0,
    learning_objectives: [],
    status: unit.status || (unit.current_content ? 'generated' : 'draft'),
    ...unit,
  } as Unit);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const paragraphsToHtml = (paragraphs: string[] = []): string =>
  paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join('');

const normalizeGeneratedUnitContent = (content: any): any => {
  if (!content || typeof content !== 'object') {
    return content;
  }

  const introduction = content.introduction
    ? {
        ...content.introduction,
        plain_text:
          content.introduction.plain_text || content.introduction.paragraphs?.join('\n\n') || '',
        html: content.introduction.html || paragraphsToHtml(content.introduction.paragraphs || []),
      }
    : content.introduction;

  const sectionPlainText = content.main_content?.sections
    ?.map((section: any) =>
      [
        section.heading,
        ...(section.paragraphs || []),
        ...(section.key_points || []).map((point: string) => `- ${point}`),
        ...(section.examples || []).map((example: string) => `Example: ${example}`),
      ]
        .filter(Boolean)
        .join('\n'),
    )
    .join('\n\n');

  const sectionHtml = content.main_content?.sections
    ?.map((section: any) =>
      [
        section.heading ? `<h3>${escapeHtml(section.heading)}</h3>` : '',
        paragraphsToHtml(section.paragraphs || []),
        section.key_points?.length
          ? `<ul>${section.key_points
              .map((point: string) => `<li>${escapeHtml(point)}</li>`)
              .join('')}</ul>`
          : '',
        section.examples?.length
          ? `<p><strong>Examples:</strong></p><ul>${section.examples
              .map((example: string) => `<li>${escapeHtml(example)}</li>`)
              .join('')}</ul>`
          : '',
      ].join(''),
    )
    .join('');

  const mainContent = content.main_content
    ? {
        ...content.main_content,
        plain_text: content.main_content.plain_text || sectionPlainText || '',
        html: content.main_content.html || sectionHtml || '',
      }
    : content.main_content;

  const summary = content.summary
    ? {
        ...content.summary,
        key_points: content.summary.key_points || content.summary.key_takeaways || [],
        plain_text:
          content.summary.plain_text ||
          [
            ...(content.summary.key_takeaways || []),
            ...(content.summary.review_questions || []),
          ].join('\n') ||
          '',
        html:
          content.summary.html ||
          [
            content.summary.key_takeaways?.length
              ? `<ul>${content.summary.key_takeaways
                  .map((takeaway: string) => `<li>${escapeHtml(takeaway)}</li>`)
                  .join('')}</ul>`
              : '',
            content.summary.review_questions?.length
              ? `<p><strong>Review Questions:</strong></p><ul>${content.summary.review_questions
                  .map((question: string) => `<li>${escapeHtml(question)}</li>`)
                  .join('')}</ul>`
              : '',
          ].join(''),
      }
    : content.summary;

  return {
    ...content,
    introduction,
    main_content: mainContent,
    summary,
  };
};

const normalizeModuleContent = (data: ModuleContentApiResponse): any => {
  const contentPointer = data.content || undefined;
  return {
    ...data,
    ...(contentPointer || {}),
    content_pointer: contentPointer,
    presigned_url: contentPointer?.presigned_url,
    content_hash: contentPointer?.content_hash,
    units: data.units?.map(normalizeUnit) || [],
  };
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const stringValue = (source: Record<string, unknown>, keys: string[], fallback = ''): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return fallback;
};

const nullableStringValue = (source: Record<string, unknown>, keys: string[]): string | null => {
  const value = stringValue(source, keys);
  return value || null;
};

const numberValue = (source: Record<string, unknown>, keys: string[], fallback: number): number => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return fallback;
};

const normalizeAssignmentSummary = (assignment: unknown): AssignmentSummary => {
  const item = asRecord(assignment);
  const module = asRecord(item.module);
  const student = asRecord(item.student);
  const moduleId = stringValue(item, ['module_id']);
  const studentId = stringValue(item, ['student_id']);
  const assignedAt = stringValue(item, ['assigned_at', 'created_at']);

  return {
    assignment_id: stringValue(item, ['assignment_id']),
    module_id: moduleId,
    module_title: stringValue(
      item,
      ['module_title', 'title'],
      stringValue(module, ['title'], moduleId),
    ),
    module_subject: stringValue(
      item,
      ['module_subject', 'subject'],
      stringValue(module, ['subject'], 'N/A'),
    ),
    module_grade_level: stringValue(
      item,
      ['module_grade_level', 'grade_level'],
      stringValue(module, ['grade_level'], 'N/A'),
    ),
    student_id: studentId,
    student_name: stringValue(
      item,
      ['student_name', 'display_name', 'name'],
      stringValue(student, ['display_name', 'name'], studentId),
    ),
    student_email: stringValue(item, ['student_email', 'email'], stringValue(student, ['email'])),
    status: stringValue(item, ['status'], 'assigned'),
    due_date: nullableStringValue(item, ['due_date']),
    assigned_at: assignedAt,
    completed_at: nullableStringValue(item, ['completed_at']),
    updated_at: stringValue(item, ['updated_at'], assignedAt),
    instructions: nullableStringValue(item, ['instructions']),
  };
};

const buildStatusSummary = (assignments: AssignmentSummary[]) => ({
  assigned: assignments.filter((assignment) => assignment.status === 'assigned').length,
  in_progress: assignments.filter((assignment) => assignment.status === 'in_progress').length,
  submitted: assignments.filter((assignment) => assignment.status === 'submitted').length,
  completed: assignments.filter((assignment) => assignment.status === 'completed').length,
});

const groupAssignmentsByModule = (assignments: AssignmentSummary[]): AssignmentModuleGroup[] => {
  const groups = new Map<string, AssignmentModuleGroup>();

  assignments.forEach((assignment) => {
    const key = assignment.module_id || assignment.assignment_id;
    const current = groups.get(key);
    if (current) {
      current.assignments.push(assignment);
      return;
    }

    groups.set(key, {
      module_id: assignment.module_id,
      module_title: assignment.module_title,
      module_subject: assignment.module_subject,
      module_grade_level: assignment.module_grade_level,
      assignments: [assignment],
    });
  });

  return Array.from(groups.values());
};

const normalizeTeacherAssignments = (data: unknown): TeacherAssignmentsResponse => {
  const payload = asRecord(data);
  const assignments = asArray(payload.assignments).map(normalizeAssignmentSummary);
  const modulesPayload = asArray(payload.modules);
  const modules =
    modulesPayload.length > 0
      ? modulesPayload.map((modulePayload) => {
          const module = asRecord(modulePayload);
          const nestedAssignments = asArray(module.assignments).map(normalizeAssignmentSummary);
          const moduleId =
            stringValue(module, ['module_id']) || nestedAssignments[0]?.module_id || '';

          return {
            module_id: moduleId,
            module_title: stringValue(
              module,
              ['module_title', 'title'],
              nestedAssignments[0]?.module_title || moduleId,
            ),
            module_subject: stringValue(
              module,
              ['module_subject', 'subject'],
              nestedAssignments[0]?.module_subject || 'N/A',
            ),
            module_grade_level: stringValue(
              module,
              ['module_grade_level', 'grade_level'],
              nestedAssignments[0]?.module_grade_level || 'N/A',
            ),
            assignments: nestedAssignments,
          };
        })
      : groupAssignmentsByModule(assignments);
  const statusSummary = asRecord(payload.status_summary);

  return {
    teacher_id: stringValue(payload, ['teacher_id']),
    assignments,
    total_assignments: numberValue(payload, ['total_assignments'], assignments.length),
    status_summary: {
      ...buildStatusSummary(assignments),
      assigned: numberValue(statusSummary, ['assigned'], buildStatusSummary(assignments).assigned),
      in_progress: numberValue(
        statusSummary,
        ['in_progress'],
        buildStatusSummary(assignments).in_progress,
      ),
      submitted: numberValue(
        statusSummary,
        ['submitted'],
        buildStatusSummary(assignments).submitted,
      ),
      completed: numberValue(
        statusSummary,
        ['completed'],
        buildStatusSummary(assignments).completed,
      ),
    },
    modules,
  };
};

const loadUnitContent = async (unit: Unit): Promise<Unit> => {
  const url = unit.current_content?.presigned_url;
  if (!url) {
    return unit;
  }

  try {
    const response = await axios.get(url);
    return {
      ...unit,
      content: normalizeGeneratedUnitContent(response.data),
      status: unit.status || 'generated',
    };
  } catch (error) {
    console.error(`Error fetching unit content for ${unit.unit_id}:`, error);
    return unit;
  }
};

/**
 * Get authentication token from AWS Amplify
 * NOTE: Cognito Authorizer requires ID Token, not Access Token
 */
const getAuthToken = async (): Promise<string> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return '';
  }
};

/**
 * Get authorization headers
 */
const getAuthHeaders = async () => ({
  'Content-Type': 'application/json',
  Authorization: `${await getAuthToken()}`,
});

/**
 * List all modules for the current teacher
 */
export const listModules = async (
  limit = 20,
  lastEvaluatedKey?: string,
): Promise<{
  modules: Module[];
  count: number;
  lastEvaluatedKey?: string;
}> => {
  try {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);

    const headers = await getAuthHeaders();
    const queryString = params.toString();
    const response = await axios.get<ApiEnvelope<ModuleListApiResponse>>(
      buildApiUrl(`modules${queryString ? `?${queryString}` : ''}`, 'v2'),
      { headers },
    );
    const data = unwrapApiData(response.data, 'Failed to list modules');
    return {
      modules: data.modules.map(normalizeModule),
      count: data.pagination.total,
    };
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to list modules'));
  }
};

/**
 * Create a new module
 */
export const createModule = async (data: CreateModuleRequest): Promise<Module> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<{ message: string; module: Module }>>(
      buildApiUrl('modules', 'v2'),
      data,
      { headers },
    );
    return normalizeModule(unwrapApiData(response.data, 'Failed to create module').module);
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to create module'));
  }
};

/**
 * Get a module by ID
 */
export const getModule = async (moduleId: string): Promise<Module> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<{ module: Module }>>(
      buildApiUrl(`modules/${moduleId}`, 'v2'),
      { headers },
    );
    return normalizeModule(unwrapApiData(response.data, 'Failed to get module').module);
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get module'));
  }
};

/**
 * Get module content
 * Fetches metadata from API, then downloads actual content from S3 presigned URL
 */
export const getModuleContent = async (moduleId: string): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<ModuleContentApiResponse>>(
      buildApiUrl(`modules/${moduleId}/content`, 'v2'),
      { headers },
    );

    const metadata = normalizeModuleContent(
      unwrapApiData(response.data, 'Failed to get module content'),
    );
    const unitsWithContent = await Promise.all(metadata.units.map(loadUnitContent));

    // If there's a presigned URL, fetch the actual content from S3
    if (metadata.presigned_url) {
      try {
        const contentResponse = await axios.get(metadata.presigned_url);
        const unitsById = new Map(unitsWithContent.map((unit: Unit) => [unit.unit_id, unit]));
        const snapshotUnits = (contentResponse.data.units || []).map((unit: any) => ({
          ...unit,
          ...(unitsById.get(unit.unit_id) || {}),
        }));
        // Merge the S3 content with the metadata
        return {
          ...metadata,
          ...contentResponse.data, // Include any other content from S3
          units: snapshotUnits.length > 0 ? snapshotUnits : unitsWithContent,
          presigned_url: metadata.presigned_url,
          content_pointer: metadata.content_pointer,
        };
      } catch (s3Error) {
        console.error('Error fetching content from S3:', s3Error);
        // Fall back to metadata if S3 fetch fails
        return {
          ...metadata,
          units: unitsWithContent,
        };
      }
    }

    return {
      ...metadata,
      units: unitsWithContent,
    };
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get module content'));
  }
};

/**
 * Create a unit within a module
 */
export const createUnit = async (moduleId: string, data: CreateUnitRequest): Promise<Unit> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<{ message: string; unit: Unit }>>(
      buildApiUrl(`modules/${moduleId}/units`, 'v2'),
      data,
      { headers },
    );
    return normalizeUnit(unwrapApiData(response.data, 'Failed to create unit').unit);
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to create unit'));
  }
};

/**
 * Generation Job Status
 */
export interface GenerationJobStatus {
  job_id: string;
  job_type: 'module_outline' | 'unit_content' | 'practice_questions';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress_percentage: number;
  progress_message: string;
  module_id: string;
  unit_id?: string;
  result?: any;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  estimated_duration_seconds: number;
  actual_duration_seconds?: number;
}

const normalizeGenerationJob = (
  job: Partial<GenerationJobStatus> & { job_id: string },
): GenerationJobStatus =>
  ({
    progress_percentage: job.status === 'completed' ? 100 : 0,
    progress_message: job.status || 'pending',
    estimated_duration_seconds: 60,
    created_at: new Date().toISOString(),
    ...job,
  } as GenerationJobStatus);

/**
 * Start async AI content generation for a unit
 * Returns a job_id that can be polled for status
 */
export const generateContentAsync = async (
  moduleId: string,
  unitId: string,
  data: GenerateContentRequest,
): Promise<{
  job_id: string;
  status: string;
  estimated_duration_seconds: number;
  poll_interval_seconds: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<
      ApiEnvelope<{
        job_id: string;
        status: string;
        estimated_duration_seconds: number;
        poll_interval_seconds: number;
      }>
    >(buildApiUrl(`modules/${moduleId}/units/${unitId}/generate-async`, 'v2'), data, { headers });
    return unwrapApiData(response.data, 'Failed to start content generation');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to start content generation'));
  }
};

/**
 * Get generation job status
 */
export const getJobStatus = async (jobId: string): Promise<GenerationJobStatus> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<{ job: GenerationJobStatus }>>(
      buildApiUrl(`generation-jobs/${jobId}`, 'v2'),
      { headers },
    );
    return normalizeGenerationJob(unwrapApiData(response.data, 'Failed to get job status').job);
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get job status'));
  }
};

/**
 * Poll for job completion
 * Returns a promise that resolves when job is completed or failed
 */
export const pollJobUntilComplete = async (
  jobId: string,
  onProgress?: (status: GenerationJobStatus) => void,
  pollIntervalMs = 3000,
  maxAttempts = 60, // 3 minutes max
): Promise<GenerationJobStatus> => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const status = await getJobStatus(jobId);

    // Call progress callback if provided
    if (onProgress) {
      onProgress(status);
    }

    // Check if job is complete
    if (status.status === 'completed') {
      return status;
    }

    // Check if job failed
    if (status.status === 'failed') {
      throw new Error(status.error_message || 'Generation failed');
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    attempts++;
  }

  throw new Error('Generation timed out. Please check job status later.');
};

/**
 * Generate AI content for a unit (legacy synchronous method - kept for backward compatibility)
 * @deprecated Use generateContentAsync and pollJobUntilComplete instead
 */
export const generateContent = async (
  moduleId: string,
  unitId: string,
  data: GenerateContentRequest,
): Promise<GenerateContentResponse> => {
  // Use async method and poll until complete
  const jobResponse = await generateContentAsync(moduleId, unitId, data);
  const finalStatus = await pollJobUntilComplete(jobResponse.job_id);

  if (finalStatus.status === 'failed') {
    throw new Error(finalStatus.error_message || 'Failed to generate content');
  }

  return (
    finalStatus.result || {
      message: 'Unit content generated successfully',
      unit_id: unitId,
      content_sections: {
        introduction: true,
        main_content: true,
        activities: 0,
        summary: true,
      },
      quiz_questions: 0,
    }
  );
};

/**
 * Publish a module
 */
export const publishModule = async (
  moduleId: string,
): Promise<{ message: string; published_at: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<{ message: string; module: Module }>>(
      buildApiUrl(`modules/${moduleId}/publish`, 'v2'),
      {},
      { headers },
    );
    const data = unwrapApiData(response.data, 'Failed to publish module');
    return {
      message: data.message,
      published_at: data.module.published_at || data.module.updated_at,
    };
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to publish module'));
  }
};

/**
 * Delete a unit from a module
 */
export const deleteUnit = async (moduleId: string, unitId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await axios.delete(buildApiUrl(`modules/${moduleId}/units/${unitId}`, 'v2'), { headers });
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to delete unit'));
  }
};

/**
 * Update unit content manually
 */
export const updateUnitContent = async (
  moduleId: string,
  unitId: string,
  content: any,
): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await axios.put(
      buildApiUrl(`modules/${moduleId}/units/${unitId}/content`, 'v2'),
      { content },
      { headers },
    );
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to update unit content'));
  }
};

/**
 * Update unit metadata (title, description, objectives, duration)
 */
export const updateUnit = async (
  moduleId: string,
  unitId: string,
  data: {
    title: string;
    description: string;
    duration_minutes: number;
    learning_objectives: string[];
  },
): Promise<Unit> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put<ApiEnvelope<{ message: string; unit: Unit }>>(
      buildApiUrl(`modules/${moduleId}/units/${unitId}`, 'v2'),
      data,
      { headers },
    );
    return normalizeUnit(unwrapApiData(response.data, 'Failed to update unit').unit);
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to update unit'));
  }
};

/**
 * Delete a module (draft only)
 */
export const deleteModule = async (moduleId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await axios.delete(buildApiUrl(`modules/${moduleId}`, 'v2'), { headers });
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to delete module'));
  }
};

/**
 * Assign a module to students
 */
export const assignModuleToStudents = async (
  moduleId: string,
  studentIds: string[],
  dueDate?: string,
  instructions?: string,
): Promise<{
  message: string;
  assignments: Array<{
    assignment_id: string;
    student_id: string;
    module_id: string;
    status: string;
    assigned_at: string;
  }>;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<any>>(
      buildApiUrl(`modules/${moduleId}/assign`, 'v2'),
      {
        student_ids: studentIds,
        due_date: dueDate,
        instructions: instructions,
      },
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to assign module');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to assign module');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Get all assignments for a module
 */
export const getModuleAssignments = async (
  moduleId: string,
): Promise<{
  module_id: string;
  module_title: string;
  assignments: Array<{
    assignment_id: string;
    student_id: string;
    student_name: string;
    student_email: string;
    status: string;
    due_date: string | null;
    assigned_at: string;
    completed_at: string | null;
    instructions: string | null;
  }>;
  total_assignments: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<any>>(
      buildApiUrl(`modules/${moduleId}/assignments`, 'v2'),
      {
        headers,
      },
    );
    return unwrapApiData(response.data, 'Failed to get module assignments');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to get module assignments');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Get all assignments for a student
 */
export const getStudentAssignments = async (
  studentId: string,
): Promise<{
  student_id: string;
  assignments: Array<{
    assignment_id: string;
    module_id: string;
    module_title: string;
    module_subject: string;
    module_grade_level: string;
    status: string;
    due_date: string | null;
    assigned_at: string;
    completed_at: string | null;
    instructions: string | null;
  }>;
  total_assignments: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<any>>(
      buildApiUrl(`students/${studentId}/assignments`, 'v2'),
      {
        headers,
      },
    );
    return unwrapApiData(response.data, 'Failed to get student assignments');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to get student assignments');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/** Get question-level practice attempts for a student assigned to the current teacher. */
export const getStudentPracticeAttempts = async (
  studentId: string,
): Promise<StudentPracticeAttemptsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<StudentPracticeAttemptsResponse>>(
      buildApiUrl(`students/${studentId}/practice-attempts`, 'v2'),
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to get student practice attempts');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get student practice attempts'));
  }
};

/**
 * Remove an assignment
 */
export const removeAssignment = async (
  assignmentId: string,
): Promise<{
  message: string;
  assignment_id: string;
  student_id: string;
  lesson_id: string;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.delete<ApiEnvelope<any>>(
      buildApiUrl(`assignments/${assignmentId}`, 'v2'),
      {
        headers,
      },
    );
    return unwrapApiData(response.data, 'Failed to remove assignment');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to remove assignment');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Get teacher's modules for a specific student
 */
export const getTeacherModulesForStudent = async (
  teacherId: string,
  studentId: string,
): Promise<{
  teacher_id: string;
  teacher_name: string;
  student_id: string;
  modules: Array<{
    module_id: string;
    title: string;
    subject: string;
    grade_level: string;
    unit_count: number;
    assignment: {
      assignment_id: string;
      status: string;
      due_date: string | null;
      instructions: string | null;
      assigned_at: string;
      updated_at: string;
      completed_at: string | null;
    };
  }>;
  total_modules: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<any>>(
      buildApiUrl(`teachers/${teacherId}/modules/student/${studentId}`, 'v2'),
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to get teacher modules');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to get teacher modules');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Update assignment status
 */
export const updateAssignmentStatus = async (
  assignmentId: string,
  status: 'in_progress' | 'completed',
  submissionData?: {
    text_response?: string;
    file_urls?: string[];
    completed_units?: string[];
  },
): Promise<{
  message: string;
  assignment: {
    assignment_id: string;
    student_id: string;
    status: string;
    updated_at: string;
    completed_at: string | null;
    submission_data?: any;
  };
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put<ApiEnvelope<any>>(
      buildApiUrl(`assignments/${assignmentId}/status`, 'v2'),
      {
        status,
        submission_data: submissionData,
      },
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to update assignment status');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to update assignment status');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Submit assignment
 */
export const submitAssignment = async (
  assignmentId: string,
  textResponse?: string,
  fileUrls?: string[],
  completedUnits?: string[],
): Promise<{
  message: string;
  assignment_id: string;
  submitted_at: string;
  status: string;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<any>>(
      buildApiUrl(`assignments/${assignmentId}/submit`, 'v2'),
      {
        text_response: textResponse,
        file_urls: fileUrls,
        completed_units: completedUnits,
      },
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to submit assignment');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to submit assignment');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Generate module outline with AI
 * Phase 2.1 Implementation
 */
export interface GenerateModuleOutlineRequest {
  subject: string;
  grade_level: string;
  topic: string;
  duration: string;
  state_standards?: string;
}

export interface ModuleOutline {
  module_overview: {
    description: string;
    importance: string;
    essential_questions: string[];
  };
  learning_objectives: string[];
  prerequisite_knowledge: string[];
  key_vocabulary: string[];
  lessons: Array<{
    lesson_number: number;
    title: string;
    description: string;
    main_concepts: string[];
    estimated_time: string;
    key_activities: string[];
  }>;
  assessment_overview: {
    formative_strategies: string[];
    summative_approach: string;
    major_projects: string[];
  };
  differentiation: {
    support_strategies: string;
    extension_strategies: string;
  };
}

export interface GenerateModuleOutlineResponse {
  message: string;
  job_id?: string;
  module_id?: string;
  outline: ModuleOutline;
  units_created?: Array<{
    unit_id: string;
    title: string;
    lesson_number: number;
    status: string;
  }>;
  total_lessons: number;
}

/**
 * Start async module outline generation
 * Returns a job_id that can be polled for status
 */
export const generateModuleOutlineAsync = async (
  moduleId: string,
  data: GenerateModuleOutlineRequest,
): Promise<{
  job_id: string;
  status: string;
  estimated_duration_seconds: number;
  poll_interval_seconds: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<
      ApiEnvelope<{
        job_id: string;
        status: string;
        estimated_duration_seconds: number;
        poll_interval_seconds: number;
      }>
    >(buildApiUrl(`modules/${moduleId}/generate-outline-async`, 'v2'), data, { headers });
    return unwrapApiData(response.data, 'Failed to start module outline generation');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to start module outline generation'));
  }
};

/**
 * Generate module outline and return the outline payload.
 * The Python async job currently returns status only, so the teacher UI uses this endpoint
 * when it needs to create units immediately from the generated outline.
 */
export const generateModuleOutline = async (
  moduleId: string,
  data: GenerateModuleOutlineRequest,
): Promise<GenerateModuleOutlineResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<GenerateModuleOutlineResponse>>(
      buildApiUrl(`modules/${moduleId}/generate-outline`, 'v2'),
      data,
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to generate module outline');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to generate module outline'));
  }
};

/**
 * Get all assignments for a teacher with student progress
 */
export const getTeacherAssignments = async (
  teacherId = 'me',
): Promise<TeacherAssignmentsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<any>>(
      buildApiUrl(`teachers/${teacherId}/assignments`, 'v2'),
      {
        headers,
      },
    );
    return normalizeTeacherAssignments(
      unwrapApiData(response.data, 'Failed to get teacher assignments'),
    );
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to get teacher assignments');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

// ========================================
// Practice Questions API Methods
// ========================================

/**
 * Generate practice questions using AI
 */
export const generatePracticeQuestions = async (
  moduleId: string,
  unitId: string,
  data: {
    question_count: number;
    difficulty: 'easy' | 'medium' | 'hard' | 'mixed';
    question_types: (
      | 'multiple_choice'
      | 'true_false'
      | 'word_problem'
      | 'numeric'
      | 'short_answer'
    )[];
    include_word_problems?: boolean;
    word_problem_contexts?: string[];
    customization_prompt?: string;
    set_title?: string;
    set_description?: string;
  },
): Promise<{
  practice_set: any;
  generation_metadata: {
    model: string;
    tokens_used: number;
    cost: number;
    generated_at: string;
  };
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<any>>(
      buildApiUrl(`modules/${moduleId}/units/${unitId}/practice-questions/generate`, 'v2'),
      data,
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to generate practice questions');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to generate practice questions');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Get practice questions for a unit
 */
export const getPracticeQuestions = async (
  moduleId: string,
  unitId: string,
): Promise<{
  practice_questions: any;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<any>>(
      buildApiUrl(`modules/${moduleId}/units/${unitId}/practice-questions`, 'v2'),
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to get practice questions');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to get practice questions');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Create a teacher-authored practice question
 */
export const createPracticeQuestion = async (
  moduleId: string,
  unitId: string,
  data: CreatePracticeQuestionRequest,
): Promise<CreatePracticeQuestionResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<CreatePracticeQuestionResponse>>(
      buildApiUrl(`modules/${moduleId}/units/${unitId}/practice-questions`, 'v2'),
      data,
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to create practice question');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to create practice question');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Update a practice question
 */
export const updatePracticeQuestion = async (
  moduleId: string,
  unitId: string,
  setId: string,
  questionId: string,
  updates: any,
): Promise<{
  message: string;
  question: any;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put<ApiEnvelope<any>>(
      buildApiUrl(
        `modules/${moduleId}/units/${unitId}/practice-questions/${setId}/questions/${questionId}`,
        'v2',
      ),
      { updates },
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to update practice question');
  } catch (error: any) {
    if (error.response) {
      throw new Error(getApiErrorMessage(error, 'Failed to update practice question'));
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Delete a practice question set
 */
export const deletePracticeQuestionSet = async (
  moduleId: string,
  unitId: string,
  setId: string,
): Promise<{
  message: string;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.delete<ApiEnvelope<any>>(
      buildApiUrl(`modules/${moduleId}/units/${unitId}/practice-questions/${setId}`, 'v2'),
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to delete practice question set');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to delete practice question set');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Submit a practice answer (for students)
 */
export const submitPracticeAnswer = async (
  moduleId: string,
  unitId: string,
  setId: string,
  questionId: string,
  answer: string | string[] | boolean | number,
  timeTakenSeconds?: number,
): Promise<SubmitPracticeAnswerResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<any>>(
      buildApiUrl(
        `modules/${moduleId}/units/${unitId}/practice-questions/${setId}/questions/${questionId}/submit`,
        'v2',
      ),
      {
        answer,
        time_taken_seconds: timeTakenSeconds,
      },
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to submit practice answer');
  } catch (error: any) {
    if (error.response) {
      throw new Error(getApiErrorMessage(error, 'Failed to submit practice answer'));
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

/**
 * Get student practice progress
 */
export const getStudentPracticeProgress = async (
  moduleId: string,
  unitId: string,
  studentId?: string,
): Promise<{
  student_id?: string;
  progress?: any;
  unit_id?: string;
  students_progress?: any[];
  total_students?: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const params = studentId ? `?student_id=${studentId}` : '';
    const response = await axios.get<ApiEnvelope<any>>(
      buildApiUrl(`modules/${moduleId}/units/${unitId}/practice-progress${params}`, 'v2'),
      { headers },
    );
    return unwrapApiData(response.data, 'Failed to get practice progress');
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to get practice progress');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};
